import type {
	PageBackgroundPatternImage,
	PageBackgroundPatternResource,
} from '@sosocio/creator-types/project';
import * as svgUtils from '@sosocio/frontend-utils/svg';
import { DOMParser } from '@xmldom/xmldom';
import { markRaw } from 'vue';
import { isSVGBackgroundPatternSource } from './background-pattern';
import { loadImage } from './image-loader';

interface BackgroundPatternCacheEntry {
	promise: Promise<PageBackgroundPatternResource>;
	settled: boolean;
}

interface BackgroundPatternDimensions {
	height: number;
	width: number;
}

const BACKGROUND_PATTERN_CACHE_LIMIT = 4;
const BACKGROUND_PATTERN_PREVIEW_MAX_DIMENSION = 1024;
const backgroundPatternCache = new Map<string, BackgroundPatternCacheEntry>();

/**
 * Creates a shared preview source for patterns whose intrinsic dimensions are
 * unnecessarily expensive for editor thumbnails.
 *
 * @param image Full-resolution pattern image returned by the source loader.
 * @returns A bounded canvas source, or `undefined` when the source is already small or cannot be rasterized.
 */
function createBackgroundPatternPreview(image: PageBackgroundPatternImage): PageBackgroundPatternImage | undefined {
	const scale = Math.min(
		1,
		BACKGROUND_PATTERN_PREVIEW_MAX_DIMENSION / image.width,
		BACKGROUND_PATTERN_PREVIEW_MAX_DIMENSION / image.height,
	);

	if (
		scale >= 1
		|| typeof document === 'undefined'
	) {
		return undefined;
	}

	try {
		const canvas = document.createElement('canvas');
		const height = Math.max(
			1,
			Math.round(image.height * scale),
		);
		const width = Math.max(
			1,
			Math.round(image.width * scale),
		);
		canvas.height = height;
		canvas.width = width;

		const context = canvas.getContext('2d');

		if (!context) {
			return undefined;
		}

		context.drawImage(
			image.image,
			0,
			0,
			width,
			height,
		);

		return {
			height,
			image: canvas,
			width,
		};
	} catch {
		return undefined;
	}
}

/**
 * Returns stable positive dimensions for a decoded image.
 *
 * @param image Loaded image whose raster dimensions are required.
 * @returns Natural dimensions, falling back to element dimensions.
 */
function getImageDimensions(image: HTMLImageElement): BackgroundPatternDimensions {
	return {
		height: image.naturalHeight || image.height,
		width: image.naturalWidth || image.width,
	};
}

/**
 * Reads one declaration from an inline SVG style attribute.
 *
 * @param style Inline style value from the root SVG element.
 * @param property CSS property to read.
 * @returns Normalized declaration value, or `null` when it is absent.
 */
function getSVGStyleDimension(
	style: string | null,
	property: 'height' | 'width',
): string | null {
	if (!style) {
		return null;
	}

	return style
		.match(new RegExp(
			`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`,
			'i',
		))?.[1]
		?.replace(
			/\s*!important\s*$/i,
			'',
		)
		.trim()
		|| null;
}

/**
 * Resolves authored SVG tile dimensions independently from a preview raster.
 * Explicit root dimensions retain the normalized image dimensions, while a
 * viewBox-only SVG uses its authored coordinate dimensions.
 *
 * @param svgContent Original serialized SVG markup.
 * @param imageDimensions Dimensions of the normalized full-resolution image.
 * @returns Logical tile dimensions used by pattern transforms.
 */
function getSVGTileDimensions(
	svgContent: string,
	imageDimensions: BackgroundPatternDimensions,
): BackgroundPatternDimensions {
	const documentModel = new DOMParser().parseFromString(
		svgContent,
		'application/xml',
	);
	const svgElement = documentModel.documentElement;
	const svgTagName = svgElement?.tagName
		.split(':')
		.pop()
		?.toLowerCase();

	if (
		!svgElement
		|| svgTagName !== 'svg'
	) {
		return imageDimensions;
	}

	const style = svgElement.getAttribute('style');
	const hasExplicitWidth = isRenderableSVGDimension(
		getSVGStyleDimension(
			style,
			'width',
		)
		|| svgElement.getAttribute('width'),
	);
	const hasExplicitHeight = isRenderableSVGDimension(
		getSVGStyleDimension(
			style,
			'height',
		)
		|| svgElement.getAttribute('height'),
	);

	if (
		hasExplicitWidth
		|| hasExplicitHeight
	) {
		return imageDimensions;
	}

	const viewBoxValues = svgElement
		.getAttribute('viewBox')
		?.trim()
		.split(/[\s,]+/)
		.map((value) => Number(value));
	const viewBoxWidth = viewBoxValues?.length === 4
		? viewBoxValues[2]
		: undefined;
	const viewBoxHeight = viewBoxValues?.length === 4
		? viewBoxValues[3]
		: undefined;
	const hasValidViewBox = (
		typeof viewBoxWidth === 'number'
		&& Number.isFinite(viewBoxWidth)
		&& viewBoxWidth > 0
		&& typeof viewBoxHeight === 'number'
		&& Number.isFinite(viewBoxHeight)
		&& viewBoxHeight > 0
	);

	return hasValidViewBox
		? {
			height: viewBoxHeight,
			width: viewBoxWidth,
		}
		: imageDimensions;
}

/**
 * Checks whether an SVG dimension is explicitly authored in a form the SVG
 * loader treats as renderable.
 *
 * @param value Root `width` or `height` attribute value.
 * @returns Whether the value can define a positive intrinsic SVG dimension.
 */
function isRenderableSVGDimension(value: string | null): boolean {
	if (!value) {
		return false;
	}

	const normalizedValue = value
		.trim()
		.toLowerCase();

	return (
		/^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?(?:px|pt|pc|mm|cm|in|q|em|ex|rem|rex|ch|vw|vh|vmin|vmax)?$/i.test(normalizedValue)
		&& Number.parseFloat(normalizedValue) > 0
	);
}

/**
 * Reuses one bounded resource per source while retaining every in-flight entry
 * until it settles.
 *
 * @param source Resolved source used as the cache key.
 * @param loadResource Source-specific resource loader.
 * @returns Shared pending or settled pattern resource.
 */
function loadCachedBackgroundPattern(
	source: string,
	loadResource: () => Promise<PageBackgroundPatternResource>,
): Promise<PageBackgroundPatternResource> {
	const cachedEntry = backgroundPatternCache.get(source);

	if (cachedEntry) {
		backgroundPatternCache.delete(source);
		backgroundPatternCache.set(
			source,
			cachedEntry,
		);

		return cachedEntry.promise;
	}

	const resourcePromise = loadResource();
	const cacheEntry: BackgroundPatternCacheEntry = {
		promise: resourcePromise,
		settled: false,
	};

	backgroundPatternCache.set(
		source,
		cacheEntry,
	);
	trimBackgroundPatternCache();
	resourcePromise.then(
		() => {
			cacheEntry.settled = true;
			trimBackgroundPatternCache();
		},
		() => {
			if (backgroundPatternCache.get(source) === cacheEntry) {
				backgroundPatternCache.delete(source);
				trimBackgroundPatternCache();
			}
		},
	);

	return resourcePromise;
}

/**
 * Deduplicates an SVG pattern load and falls back to the generic image loader
 * when SVG metadata cannot be read.
 *
 * @param source Resolved SVG source.
 * @returns Shared pattern resource with stable intrinsic dimensions.
 */
function loadSVGBackgroundPattern(source: string): Promise<PageBackgroundPatternResource> {
	return loadCachedBackgroundPattern(
		source,
		() => svgUtils
			.load(source)
			.then(({ content, image }) => {
				const imageDimensions = getImageDimensions(image);

				return makeBackgroundPatternResource(
					image,
					getSVGTileDimensions(
						content,
						imageDimensions,
					),
				);
			})
			.catch(async () => {
				const image = await loadImage(source);
				const dimensions = getImageDimensions(image);

				return makeBackgroundPatternResource(
					image,
					dimensions,
				);
			}),
	);
}

/**
 * Converts a decoded image and authored tile dimensions into the transient
 * page resource used by canvas and CSS background renderers.
 *
 * @param image Loaded full-resolution image.
 * @param dimensions Authored dimensions that determine pattern repetition.
 * @returns Non-reactive pattern resource with an optional bounded preview.
 */
function makeBackgroundPatternResource(
	image: HTMLImageElement,
	dimensions: BackgroundPatternDimensions,
): PageBackgroundPatternResource {
	const imageDimensions = getImageDimensions(image);
	const imageModel: PageBackgroundPatternImage = {
		height: imageDimensions.height,
		image,
		width: imageDimensions.width,
	};

	return markRaw({
		height: dimensions.height,
		image: imageModel,
		preview: createBackgroundPatternPreview(imageModel),
		width: dimensions.width,
	});
}

/**
 * Removes least-recently-used settled entries beyond the resource limit
 * without breaking in-flight deduplication.
 */
function trimBackgroundPatternCache(): void {
	let settledEntryCount = Array.from(backgroundPatternCache.values())
		.filter((entry) => entry.settled)
		.length;

	if (settledEntryCount <= BACKGROUND_PATTERN_CACHE_LIMIT) {
		return;
	}

	for (
		const [
			source,
			entry,
		] of backgroundPatternCache
	) {
		if (!entry.settled) {
			continue;
		}

		backgroundPatternCache.delete(source);
		settledEntryCount -= 1;

		if (settledEntryCount <= BACKGROUND_PATTERN_CACHE_LIMIT) {
			return;
		}
	}
}

/**
 * Loads one background tile with stable authored dimensions, bounded preview
 * generation, in-flight deduplication, and settled-resource LRU caching.
 *
 * SVG sources retain normalized explicit dimensions or viewBox-only authored
 * dimensions. Raster sources retain the decoded image dimensions.
 *
 * @param source Resolved background pattern URL or data URL.
 * @returns Shared pattern resource used by canvas and CSS renderers.
 */
export function loadBackgroundPattern(source: string): Promise<PageBackgroundPatternResource> {
	if (isSVGBackgroundPatternSource(source)) {
		return loadSVGBackgroundPattern(source);
	}

	return loadCachedBackgroundPattern(
		source,
		async () => {
			const image = await loadImage(source);
			const dimensions = getImageDimensions(image);

			return makeBackgroundPatternResource(
				image,
				dimensions,
			);
		},
	);
}

export default loadBackgroundPattern;
